import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isCancel } from 'axios';

import { PAYRUN_QUERY_KEYS, payrunQueryKeys } from '@xemplo/gp-constants';
import {
  AmendmentExtended,
  GpEarningLineDetailVarianceResponse,
  GpWorker,
  GpWorkerDetail,
  ListPagedResult,
  PAYRUN_STATUS_IDS,
  PayrunChangeLogItem,
  PayrunEarningLine,
  PayrunFile,
  PayrunHeader,
  PayrunInvoices,
  PayrunMilestone,
  PayrunSummary,
  Result,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { PayrunAPI } from './payrun-query.constants';
import {
  emptyPayrunSummaryResponse,
  emptyPayrunWorkerDetailsResponse,
  mapMilestoneSteps,
  parseEarningLineDetailsRows,
} from './payrun-query.helpers';
import {
  UseApproveEarningLinesProps,
  UseGetPayrunAmendmentsQueryProps,
  UseGetPayrunChangeLogQueryProps,
  UseGetPayrunEarningLineDetailsVarianceQueryProps,
  UseGetPayrunEarningLinesQueryProps,
  UseGetPayrunFilesQueryProps,
  UseGetPayrunHeaderQueryProps,
  UseGetPayrunInvoicesQueryProps,
  UseGetPayrunMilestonesQueryProps,
  UseGetPayrunSummaryQueryProps,
  UseGetPayrunWorkerDetailsQueryProps,
  UseGetPayrunWorkersQueryProps,
  UseRejectEarningLinesProps,
  UseSubmitAmendmentsProps,
} from './payrun-query.types';

/**
 * Gets all workers for a given payrun
 */
export const useGetPayrunWorkersQuery = (props: UseGetPayrunWorkersQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;

  const url = apiUrlBuilder(
    PayrunAPI.workersByPayrunId(id),
    applicationApiUrl,
    requestParams,
  );

  return useQuery<ListPagedResult<GpWorker>>({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKERS, url, requestParams],
    queryFn: () => client.get<ListPagedResult<GpWorker>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

export const useGetPayrunWorkerDetailsQuery = (
  props: UseGetPayrunWorkerDetailsQueryProps,
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { workerId, id } = props;

  const url = apiUrlBuilder(PayrunAPI.workerDetailsById(id, workerId), applicationApiUrl);

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_WORKER_DETAILS, url],
    queryFn: ({ signal }) => {
      return client
        .get<Result<GpWorkerDetail>>(url, { signal })
        .then((resp) => resp.data)
        .catch((err) => {
          if (isCancel(err)) {
            return emptyPayrunWorkerDetailsResponse;
          }
          throw err;
        });
    },
    enabled: !!(id && applicationApiUrl),
  });
};
/**
 * Gets all amendments for a given payrun
 */
export const useGetPayrunAmendmentsQuery = (props: UseGetPayrunAmendmentsQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;

  const url = apiUrlBuilder(
    PayrunAPI.amendmentsByPayrunId(id),
    applicationApiUrl,
    requestParams,
  );

  return useQuery<ListPagedResult<AmendmentExtended>>({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_AMENDMENTS, url, requestParams],
    queryFn: () =>
      client.get<ListPagedResult<AmendmentExtended>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

export const useGetPayrunFilesQuery = (
  props: UseGetPayrunFilesQueryProps,
  payrunStatusId: number | null,
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(
    PayrunAPI.filesByPayrunId(id),
    applicationApiUrl,
    requestParams,
  );

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_FILES, url, requestParams],
    queryFn: () => client.get<Result<PayrunFile[]>>(url).then((resp) => resp.data),
    enabled: !!(
      id &&
      applicationApiUrl &&
      payrunStatusId &&
      payrunStatusId > PAYRUN_STATUS_IDS.CONFIRMED_BY_PAYROLL_ADMIN_USER_TRIGGERED
    ),
  });
};

export const useGetPayrunMilestonesQuery = (props: UseGetPayrunMilestonesQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(
    PayrunAPI.milestonesByPayrunId(id),
    applicationApiUrl,
    requestParams,
  );

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_MILESTONES, url, requestParams],
    queryFn: () => client.get<Result<PayrunMilestone>>(url).then((resp) => resp.data),
    enabled: !!id && !!applicationApiUrl,
    select: (data) => mapMilestoneSteps(data.result),
  });
};

export const useGetPayrunHeaderQuery = (props: UseGetPayrunHeaderQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;
  const url = apiUrlBuilder(PayrunAPI.headerByPayrunId(id), applicationApiUrl);

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_HEADER, url],
    queryFn: () => client.get<Result<PayrunHeader>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

export const useGetPayrunChangeLogQuery = (props: UseGetPayrunChangeLogQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(
    PayrunAPI.changeLogByPayrunId(id),
    applicationApiUrl,
    requestParams,
  );

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_CHANGE_LOG, url, requestParams],
    queryFn: () =>
      client.get<ListPagedResult<PayrunChangeLogItem>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

export const useGetPayrunEarningLinesQuery = (
  props: UseGetPayrunEarningLinesQueryProps,
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, id } = props;
  const url = apiUrlBuilder(
    PayrunAPI.earningLinesByPayrunId(id),
    applicationApiUrl,
    requestParams,
  );
  return useQuery<ListPagedResult<PayrunEarningLine>>({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_EARNING_LINES, url, requestParams],
    queryFn: () =>
      client.get<ListPagedResult<PayrunEarningLine>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

export const useGetPayrunEarningLineDetailsVarianceQuery = (
  props: UseGetPayrunEarningLineDetailsVarianceQueryProps,
) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id, workerId } = props;
  const url = apiUrlBuilder(
    PayrunAPI.earningLineDetailsVarianceById(id, workerId),
    applicationApiUrl,
  );
  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_EARNING_LINE_DETAILS_VARIANCE, url],
    queryFn: () =>
      client
        .get<Result<GpEarningLineDetailVarianceResponse>>(url)
        .then((resp) => resp.data),
    enabled: !!id && !!applicationApiUrl && !!workerId,
    select: (data) => ({
      ...data,
      result: {
        ...data.result,
        parsedRows: parseEarningLineDetailsRows(
          data.result?.payrunEmployeeEarninglineItems,
        ),
      },
    }),
  });
};

export const useGetPayrunSummaryQuery = (props: UseGetPayrunSummaryQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;

  const url = apiUrlBuilder(PayrunAPI.summaryByPayrunId(id), applicationApiUrl);

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_SUMMARY, url],
    queryFn: ({ signal }) => {
      return client
        .get<Result<PayrunSummary>>(url, { signal })
        .then((resp) => resp.data)
        .catch((err) => {
          if (isCancel(err)) {
            return emptyPayrunSummaryResponse;
          }
          throw err;
        });
    },
    enabled: !!(id && applicationApiUrl),
  });
};
export const useGetPayrunInvoicesQuery = (props: UseGetPayrunInvoicesQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;

  const url = apiUrlBuilder(PayrunAPI.invoiceByPayrunId(id), applicationApiUrl);

  return useQuery({
    queryKey: [PAYRUN_QUERY_KEYS.GET_PAYRUN_INVOICE, url],
    queryFn: () => client.get<Result<PayrunInvoices>>(url).then((resp) => resp.data),
    enabled: !!(id && applicationApiUrl),
  });
};

/**
 * Hook submitting all amendments for a payrun in amendment stage. Progresses the payrun to the next stage.
 * @param props UseSubmitAmendmentsProps
 * @returns useMutation hook (Tanstack query) for submitting the post request
 */
export const useSubmitAmendments = ({
  onSuccess,
  onError,
  payrunId,
}: UseSubmitAmendmentsProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(
    PayrunAPI.submitAmendmentsByPayrunId(payrunId),
    applicationApiUrl,
  );
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.post(url).then((resp) => resp.data),
    onSuccess: (data, variables, onMutateResult, context) => {
      onSuccess?.(data, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: payrunQueryKeys.details() });
    },
    onError,
  });
};

/** Approve payrun during review stage  */
export const useApproveEarningLines = ({
  onSuccess,
  onError,
  payrunId,
}: UseApproveEarningLinesProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(
    PayrunAPI.approveEarningLinesByPayrunId(payrunId),
    applicationApiUrl,
  );
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.post(url).then((resp) => resp.data),
    onSuccess: (data, variables, onMutateResult, context) => {
      onSuccess?.(data, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: payrunQueryKeys.details() });
    },
    onError,
  });
};

/** Mark invoice as paid */
export const usePayInvoice = ({
  onSuccess,
  onError,
  payrunId,
}: UseRejectEarningLinesProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const url = apiUrlBuilder(PayrunAPI.payInvoiceByPayrunId(payrunId), applicationApiUrl);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.post(url).then((resp) => resp.data),
    onSuccess: (data, variables, onMutateResult, context) => {
      onSuccess?.(data, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: payrunQueryKeys.details() });
    },
    onError,
  });
};
